import { callGeotabAPI } from '../api/geotab-client';
import { buildGroupHierarchy, isSystemGroupId } from '../api/group-hierarchy';
import type { GroupHierarchy } from '../api/group-hierarchy';
import type { Device, Group } from '../api/types';
import { ARCHIVED_SERIAL, isGeotabHardwareDevice } from './reset-device-config';
import type { RdRow, RdFormValues, RawDevice } from './reset-device-types';

export interface RdData {
  rows:      RdRow[];
  hierarchy: GroupHierarchy;
  /** Active devices hidden because they aren't Geotab hardware. Surfaced in the
   *  toolbar so a missing vehicle reads as "filtered", not "lost". */
  excludedNonGeotab: number;
}

// ── Read ──────────────────────────────────────────────────────────────────

/**
 * Load active devices plus the group tree. Self-contained by design (mirrors
 * watchdog-data.ts): the page must work on a cold open without waiting for
 * Vehicle Health to have populated `state.allVehicles`.
 */
export async function loadResetDeviceData(): Promise<RdData> {
  const [devices, rawGroups] = await Promise.all([
    // No resultsLimit — `Get` has no pagination, so any cap silently truncates.
    // fleet-data.ts:76 and watchdog-data.ts:58 already fetch Device uncapped.
    callGeotabAPI<Device[]>('Get', { typeName: 'Device' }),
    callGeotabAPI<Group[]>('Get', { typeName: 'Group', resultsLimit: 1000 }),
  ]);

  const hierarchy = buildGroupHierarchy(rawGroups ?? []);
  const now = new Date();

  // Same active-device predicate as fleet-data.ts:77-81. Archived devices must
  // never be listed here — resetting an already-archived record would strand a
  // serial that has, by definition, already been freed.
  const active = (devices ?? []).filter((d) => {
    if (d.activeTo && new Date(d.activeTo) <= now) return false;
    if (!d.serialNumber) return false;
    return true;
  });

  const hardware = active.filter((d) => isGeotabHardwareDevice(d.deviceType));
  const excludedNonGeotab = active.length - hardware.length;

  const rows: RdRow[] = hardware
    .map((d) => ({
      id:           d.id,
      name:         d.name || d.serialNumber,
      serialNumber: d.serialNumber,
      vin:          d.vehicleIdentificationNumber || '—',
      deviceType:   d.deviceType || '—',
      // Customer groups only — the built-in asset-type/company groups every
      // device carries would drown out the one name that identifies the vehicle.
      groupNames:   (d.groups ?? [])
        .filter((g) => g.id && !isSystemGroupId(g.id))
        .map((g) => hierarchy.groupMap.get(g.id))
        .filter((n): n is string => !!n)
        .join(', ') || '—',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { rows, hierarchy, excludedNonGeotab };
}

// ── Audit comment ─────────────────────────────────────────────────────────

/** ISO calendar date. Deliberately locale-independent: this string is written
 *  into a permanent Geotab record that may be read from any region. */
export function formatAuditDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The audit line, in the exact wording the business requires.
 *
 * It is written during the archive, i.e. before the replacement exists, so in
 * the rare case where the re-add fails it briefly overstates what happened.
 * That is handled by amending the record rather than by hedging the wording:
 * see buildFailedReAddNote / buildResolvedNote below.
 */
export function buildAuditComment(serialNumber: string, userName: string, today: Date): string {
  return `${serialNumber} archived and re-added by ${userName || 'unknown user'} on ${formatAuditDate(today)}`;
}

/** Appended only if the re-add fails, so the record never lies for long. */
export function buildFailedReAddNote(): string {
  return 'CORRECTION: the re-add did not complete — no replacement record was created.';
}

/** Appended if a later retry succeeds, closing out a previous CORRECTION line. */
export function buildResolvedNote(newDeviceId: string, newName: string): string {
  return `Replacement created on retry: ${newName}${newDeviceId ? ` (id ${newDeviceId})` : ''}`;
}

// ── Write ─────────────────────────────────────────────────────────────────

/**
 * Archive the old record so its history stays attributed to the old vehicle,
 * and free its serial number for reuse.
 *
 * Geotab's `Set` REPLACES the entire entity. The device is therefore re-read and
 * mutated in place — constructing a fresh literal from our narrow `Device`
 * interface would null out name, groups, VIN, licence plate and every other
 * field we don't model, destroying exactly the history this feature promises to
 * keep. Do not "tidy" this into an object literal.
 */
export async function archiveDevice(
  deviceId: string,
  auditLine: string,
  expectedSerial: string,
): Promise<void> {
  const found = await callGeotabAPI<RawDevice[]>('Get', {
    typeName: 'Device',
    search: { id: deviceId },
  });
  const full = found?.[0];
  if (!full) throw new Error(`Device could not be re-read before archiving (id ${deviceId}).`);

  // Last-moment guard against acting on a stale list: someone else may have
  // archived or re-serialled this device since the page loaded. Cheap to check,
  // and the only place it can still be checked for free — the Get is happening
  // anyway.
  const nowSerial = typeof full.serialNumber === 'string' ? full.serialNumber : '';
  if (nowSerial !== expectedSerial) {
    throw new Error(
      `This device now has serial "${nowSerial}", not "${expectedSerial}". `
      + 'Someone may have changed it. Refresh the list and try again.',
    );
  }
  const activeTo = typeof full.activeTo === 'string' ? full.activeTo : '';
  if (activeTo && new Date(activeTo) <= new Date()) {
    throw new Error('This device has already been archived. Refresh the list and try again.');
  }

  const existing = typeof full.comment === 'string' ? full.comment.trim() : '';
  full.comment      = existing ? `${existing}\n${auditLine}` : auditLine;   // append, never overwrite
  full.serialNumber = ARCHIVED_SERIAL;
  full.activeTo     = new Date().toISOString();

  await callGeotabAPI('Set', { typeName: 'Device', entity: full });
}

/**
 * Create the replacement record, reusing the serial the archive just freed.
 * Returns the new device id.
 */
export async function addReplacementDevice(form: RdFormValues): Promise<string> {
  const groups = [
    { id: form.assetTypeGroupId },
    ...form.groupIds
      .filter((id) => id && id !== form.assetTypeGroupId)
      .map((id) => ({ id })),
  ];

  const result = await callGeotabAPI<unknown>('Add', {
    typeName: 'Device',
    entity: {
      name:                        form.name,
      serialNumber:                form.serialNumber,
      vehicleIdentificationNumber: form.vin,
      groups,
    },
  });

  // Add returns the new id as a bare string; some builds wrap it in { id }.
  if (typeof result === 'string') return result;
  if (result && typeof result === 'object' && 'id' in result) {
    return String((result as { id: unknown }).id);
  }
  return '';
}

/** Append a line to a device's comment, round-tripping the entity as `Set` requires. */
export async function appendDeviceComment(deviceId: string, line: string): Promise<void> {
  const found = await callGeotabAPI<RawDevice[]>('Get', {
    typeName: 'Device',
    search: { id: deviceId },
  });
  const full = found?.[0];
  if (!full) throw new Error(`Device ${deviceId} not found when appending comment.`);

  const existing = typeof full.comment === 'string' ? full.comment.trim() : '';
  full.comment = existing ? `${existing}\n${line}` : line;
  await callGeotabAPI('Set', { typeName: 'Device', entity: full });
}

// ── Permission faults ─────────────────────────────────────────────────────
// Geotab answers an unauthorised write with InvalidPermissionsException, whose
// message is ~50 lines: one useful sentence naming the clearances required,
// followed by "Access list:" and a dump of every clearance the user *does*
// have. Rendered raw it buries the actual problem, so the useful part is
// extracted and the dump is tucked behind a disclosure.

const PERMISSION_FAULT = /InvalidPermissionsException/i;

export interface RdPermissionFault {
  isPermission: boolean;
  /** Clearances that would satisfy the operation — any ONE of them is enough. */
  clearances: string[];
}

export function describePermissionFault(raw: string): RdPermissionFault {
  if (!PERMISSION_FAULT.test(raw)) return { isPermission: false, clearances: [] };

  // "... requested operation: A or B or C Called by: X Access list: ..."
  const m = /requested operation:\s*([\s\S]*?)(?:\s*Called by:|\s*Access list:|$)/i.exec(raw);
  const clearances = m
    ? m[1].split(/\s+or\s+/i).map((s) => s.trim()).filter(Boolean)
    : [];

  return { isPermission: true, clearances };
}

// ── Reconciliation ────────────────────────────────────────────────────────
// A rejected request does NOT prove the write did not land: Geotab can commit a
// Set or Add and still have the response time out. Reporting "nothing was
// changed" off the back of a caught error is therefore a guess, and on an
// irreversible operation it is the most damaging guess available.
//
// So after any failure the real state is read back rather than inferred. This
// also removes the need for durable client-side recovery state — there is no
// point remembering what we think happened when we can go and look.

export type RdReconciled =
  | { kind: 'untouched';  }                                   // original still active — safe to retry
  | { kind: 'archived';   }                                   // archived, no replacement yet
  | { kind: 'completed';  newId: string; newName: string }    // succeeded despite the client error
  | { kind: 'conflict';   detail: string }                    // ambiguous — escalate, do not auto-retry
  | { kind: 'unknown';    detail: string };                   // reconciliation itself failed

function isActive(d: RawDevice, now: Date): boolean {
  const activeTo = typeof d.activeTo === 'string' ? d.activeTo : '';
  return !activeTo || new Date(activeTo) > now;
}

/**
 * Determine what actually happened to `deviceId` / `serialNumber`.
 * Ordering matters: a live device on the original serial means the whole
 * operation succeeded, and that outcome outranks the state of the old record.
 */
export async function reconcileResetState(
  deviceId: string,
  serialNumber: string,
): Promise<RdReconciled> {
  try {
    const [byId, bySerial] = await Promise.all([
      callGeotabAPI<RawDevice[]>('Get', { typeName: 'Device', search: { id: deviceId } }),
      callGeotabAPI<RawDevice[]>('Get', { typeName: 'Device' }),
    ]);

    const now = new Date();
    const original = byId?.[0];

    // Geotab has no serial search operator, so match client-side over the fleet.
    const liveOnSerial = (bySerial ?? []).filter(
      (d) => d.serialNumber === serialNumber && d.id !== deviceId && isActive(d, now),
    );

    if (liveOnSerial.length > 1) {
      return {
        kind: 'conflict',
        detail: `${liveOnSerial.length} active devices already carry serial ${serialNumber}. `
              + 'Resolve the duplicates in MyGeotab before retrying.',
      };
    }

    if (liveOnSerial.length === 1) {
      const rep = liveOnSerial[0];
      return {
        kind: 'completed',
        newId:   String(rep.id ?? ''),
        newName: typeof rep.name === 'string' ? rep.name : serialNumber,
      };
    }

    if (!original) {
      return { kind: 'conflict', detail: `Original device ${deviceId} can no longer be read.` };
    }

    if (isActive(original, now) && original.serialNumber === serialNumber) {
      return { kind: 'untouched' };
    }

    if (!isActive(original, now)) {
      return { kind: 'archived' };
    }

    return {
      kind: 'conflict',
      detail: `Original device is still active but its serial is now `
            + `"${String(original.serialNumber ?? '')}" rather than "${serialNumber}".`,
    };
  } catch (err) {
    // Reconciliation failed too (offline, session lost). Say so plainly rather
    // than falling back to a guess.
    return { kind: 'unknown', detail: (err as Error).message };
  }
}
